import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './EpicPanel.css';

/**
 * Epic Panel Component - Displays epics in a side panel
 * Used in both Sprint Board and Backlog views for consistent epic visibility
 * 
 * Domain Rule: Epics should never appear in sprint/backlog lists
 * They are shown separately in this dedicated panel when enabled
 */
const EpicPanel = ({ 
  projectId, 
  isOpen, 
  onClose, 
  onEpicClick,
  className = '' 
}) => {
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch epics when panel opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchEpics();
    }
  }, [isOpen, projectId]);

  const fetchEpics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/hierarchy/projects/${projectId}/epics/panel`);
      setEpics(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch epics:', err);
      setError('Failed to load epics');
    } finally {
      setLoading(false);
    }
  };

  const handleEpicClick = (epic, e) => {
    if (onEpicClick) {
      onEpicClick(epic, e);
    }
  };

  return (
    <aside 
      className={`epic-panel ${isOpen ? 'open' : ''} ${className}`} 
      aria-hidden={!isOpen}
    >
      <div className="epic-panel-header">
        <span className="epic-panel-title">Epics</span>
        <button
          className="epic-panel-close"
          onClick={onClose}
          title="Hide epic panel"
          aria-label="Close epic panel"
        >
          ×
        </button>
      </div>
      
      <div className="epic-panel-body">
        {loading && (
          <div className="epic-panel-state">Loading epics...</div>
        )}
        
        {!loading && error && (
          <div className="epic-panel-state error">{error}</div>
        )}
        
        {!loading && !error && epics.length === 0 && (
          <div className="epic-panel-state">No epics yet</div>
        )}
        
        {!loading && !error && epics.length > 0 && (
          <div className="epic-panel-list">
            {epics.map((epic) => {
              const progress = epic.progress_percent || 0;
              return (
                <div key={epic.id} className="epic-panel-item">
                  <div className="epic-panel-item-header">
                    <button
                      className="epic-panel-link"
                      onClick={(e) => handleEpicClick(epic, e)}
                      title={`${epic.task_key} - ${epic.title}`}
                    >
                      <span className="epic-key">{epic.task_key}</span>
                      <span className="epic-title">{epic.title}</span>
                    </button>
                    <button
                      className="epic-panel-edit"
                      onClick={(e) => handleEpicClick(epic, e)}
                      title="Quick edit"
                      aria-label={`Edit ${epic.task_key}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="epic-panel-meta">
                    <span className="epic-status">{epic.status}</span>
                    <span>{epic.story_count} stories</span>
                    <span>{epic.child_count} issues</span>
                    <span>{progress}%</span>
                  </div>
                  
                  <div 
                    className="epic-panel-progress" 
                    role="progressbar" 
                    aria-valuemin="0" 
                    aria-valuemax="100" 
                    aria-valuenow={progress}
                  >
                    <div 
                      className="epic-panel-progress-bar" 
                      style={{ width: `${progress}%` }} 
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
};

export default EpicPanel;
